import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Center, Flex, Spacer, Text } from '@chakra-ui/react';
import { Trans } from 'react-i18next';
import AudioIndicator from './audioIndicator';
import './videoCSS.css';
import { UserIcon } from '../../images';
import ParticipantMenu from './participantMenu';
import { useTrack } from '../../twilio/hooks';
import { SessionType } from '../../redux/reducers';

const translateRole = (role) => {
  if (['deaf', 'interpreter', 'hears'].includes(role)) {
    return <Trans i18nKey={`common.usertype.${role}`} />;
  } else {
    return <>{role || ''}</>;
  }
};

const ParticipantUI = ({ member, useVideo, isSmall, ...containerProps }) => {
  const videoRef = useRef();
  const roles = useSelector((state) => state.activeSession.roles);
  const role = roles[member && member.name];
  const sessionType = useSelector((state) => state.activeSession.type);

  const name = member && member.name;
  const trackSelector = useCallback(
    (track, participant) => {
      return participant.identity === name && track.kind === 'video';
    },
    [name]
  );
  const videoTrack = useTrack(trackSelector);
  const isVideoEnabled = videoTrack && videoTrack.isEnabled;

  useEffect(() => {
    if (!videoTrack || !isVideoEnabled) {
      return;
    }
    const videoElement = videoTrack.attach(videoRef.current);
    if (member.isSelf) {
      videoElement.style.transform = 'scale(-1, 1)';
    }
    return () => {
      videoTrack.detach(videoElement);
    };
  }, [videoTrack, isVideoEnabled]);

  // render placeholder
  if (member == null || useVideo === false) {
    return (
      <Center borderWidth="1px" height="100%" width="100%">
        <Text color="white">{name}</Text>
      </Center>
    );
  }

  return (
    <Box borderWidth="1px" overflow="hidden" height="100%" position="relative" {...containerProps}>
      {isVideoEnabled ? (
        <>
          {/*eslint-disable-next-line*/}
          <video className="participantVideo" ref={videoRef} autoPlay={true} />
        </>
      ) : (
        <Flex
          height="100%"
          width="100%"
          justifyContent="space-around"
          alignItems="center"
          bg="primary"
        >
          <Box color="white">
            <UserIcon />
          </Box>
        </Flex>
      )}
      <Box bg="#34333" width="100%" height="100%" position="absolute" top={0}>
        {isSmall ? (
          <Box height="100%" _hover={{ '& > div': { visibility: 'visible' } }}>
            <Flex
              direction="column"
              height="100%"
              visibility={isVideoEnabled ? 'hidden' : 'visible'}
            >
              <Flex py="1" px="0" bg={isVideoEnabled ? '#0008' : 'none'} color="white">
                <Box flexGrow="1" mx="0">
                  <AudioIndicator member={member} />
                </Box>
                <Spacer />
                <Text mx="1">{translateRole(role)}</Text>
              </Flex>
              <Spacer />
              <Text color="white" whiteSpace="nowrap" px="1" overflow="hidden">
                {name}
              </Text>
            </Flex>
            {sessionType === SessionType.VIDEO && (
              <Box position="absolute" top="0" left="0" bottom="0" right="0">
                <ParticipantMenu
                  visibility="hidden"
                  member={member}
                  isSmall={true}
                  buttonProps={{ width: '100%', height: '100%' }}
                  placement="right"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box height="100%" _hover={{ '& > div': { visibility: 'visible' } }}>
            <Flex direction="column" visibility="hidden">
              <Flex py="1" px="3" bg="#0008" color="white">
                <Text>{name}</Text>
                <Box flexGrow="1" mx="4">
                  <AudioIndicator member={member} />
                </Box>
                <Spacer />
                <Text>{translateRole(role)}</Text>
              </Flex>
              <Box alignSelf="flex-end">
                <ParticipantMenu opacity="0.5" member={member} isSmall={false} />
              </Box>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};

ParticipantUI.propTypes = {
  member: PropTypes.object,
  useVideo: PropTypes.bool,
  isSmall: PropTypes.bool,
};

ParticipantUI.defaultProps = {
  member: null,
  useVideo: false,
  isSmall: false,
};

export default ParticipantUI;
